import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Users,
  MessageSquare,
  Mail,
  Phone,
  Filter,
  Search,
  Play,
  Pause,
  MoreVertical,
  Star,
  FileText,
  Calendar,
  User,
  Building,
  Zap,
  Target,
  BarChart3,
  Edit,
  Trash2,
  Plus,
  ArrowRight,
  Check,
  X,
  Sparkles,
  Award,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/Button/Button';
import { cn } from '../utils/cn';

// Review item interface
interface ReviewItem {
  id: string;
  type: 'email' | 'sms' | 'linkedin' | 'voicemail';
  title: string;
  content: string;
  status: 'pending' | 'in-review' | 'approved' | 'rejected' | 'needs-changes';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  openRate: number;
  responseRate: number;
  brandScore: number;
  requester: string;
  assignedTo?: string;
  createdAt: string;
  deadline: string;
  timeLeft: string;
  complianceIssues: string[];
}

interface ReviewStats {
  totalItems: number;
  pendingReview: number;
  inReview: number;
  overdue: number;
  avgQualityScore: number;
  approvalRate: number;
  avgTimeToReview: number;
}

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; suffix?: string; decimals?: number }> = ({ 
  end, 
  duration = 2000, 
  suffix = "",
  decimals = 0
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const currentCount = progress * end;
      setCount(decimals > 0 ? Number(currentCount.toFixed(decimals)) : Math.floor(currentCount));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, decimals]);

  return (
    <span className="font-bold">
      {decimals > 0 ? count.toFixed(decimals) : count.toLocaleString()}{suffix}
    </span>
  );
};

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  icon: React.ElementType;
  gradient: string;
  description?: string;
  decimals?: number;
}> = ({ title, value, change, icon: Icon, gradient, description, decimals = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} decimals={decimals} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">{change}</span>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

// Review Item Card Component
const ReviewItemCard: React.FC<{
  item: ReviewItem;
  onReview: (item: ReviewItem) => void;
  onApprove: (item: ReviewItem) => void;
  onReject: (item: ReviewItem) => void;
}> = ({ item, onReview, onApprove, onReject }) => {
  const [showActions, setShowActions] = useState(false);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return { color: 'text-blue-400 bg-blue-500/20 border-blue-500/30', label: 'Pending' };
      case 'in-review':
        return { color: 'text-purple-400 bg-purple-500/20 border-purple-500/30', label: 'In Review' };
      case 'approved':
        return { color: 'text-green-400 bg-green-500/20 border-green-500/30', label: 'Approved' };
      case 'rejected':
        return { color: 'text-red-400 bg-red-500/20 border-red-500/30', label: 'Rejected' };
      default:
        return { color: 'text-orange-400 bg-orange-500/20 border-orange-500/30', label: 'Needs Changes' };
    }
  };

  const getPriorityConfig = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return { color: 'text-red-400', bg: 'bg-red-500/20' };
      case 'high':
        return { color: 'text-orange-400', bg: 'bg-orange-500/20' };
      case 'medium':
        return { color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
      default:
        return { color: 'text-green-400', bg: 'bg-green-500/20' };
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email': return <Mail className="w-5 h-5" />;
      case 'sms': return <MessageSquare className="w-5 h-5" />;
      case 'linkedin': return <Building className="w-5 h-5" />;
      case 'voicemail': return <Phone className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'email': return 'text-blue-500 bg-blue-500/20';
      case 'sms': return 'text-green-500 bg-green-500/20';
      case 'linkedin': return 'text-purple-500 bg-purple-500/20';
      case 'voicemail': return 'text-orange-500 bg-orange-500/20';
      default: return 'text-neutral-500 bg-neutral-500/20';
    }
  };

  const statusConfig = getStatusConfig(item.status);
  const priorityConfig = getPriorityConfig(item.priority);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.01 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/60 to-neutral-800/40 backdrop-blur-xl border border-neutral-700/40 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-lg hover:shadow-xl"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3 mb-2">
            <div className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              getTypeColor(item.type)
            )}>
              {getTypeIcon(item.type)}
            </div>
            <h3 className="text-lg font-semibold text-white truncate">
              {item.title}
            </h3>
          </div>
          
          <div className="flex items-center space-x-2 mb-3">
            <div className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border backdrop-blur-xl",
              statusConfig.color
            )}>
              <div className="w-2 h-2 rounded-full bg-current mr-2" />
              {statusConfig.label}
            </div>
            <div className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
              priorityConfig.color,
              priorityConfig.bg
            )}>
              {item.priority} priority
            </div>
          </div>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          
          <AnimatePresence>
            {showActions && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute right-0 top-full mt-2 w-48 bg-neutral-900/95 backdrop-blur-xl border border-neutral-700 rounded-xl shadow-2xl z-10"
              >
                <div className="p-2">
                  <button
                    onClick={() => onReview(item)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">Review</span>
                  </button>
                  <button
                    onClick={() => onApprove(item)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-lg transition-colors text-left"
                  >
                    <Check className="w-4 h-4" />
                    <span className="text-sm">Approve</span>
                  </button>
                  <button
                    onClick={() => onReject(item)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors text-left"
                  >
                    <X className="w-4 h-4" />
                    <span className="text-sm">Reject</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Content Preview */}
      <div className="mb-4 p-3 bg-neutral-800/30 rounded-lg">
        <p className="text-neutral-300 text-sm line-clamp-3">
          {item.content}
        </p>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-blue-400">
            {item.openRate}%
          </p>
          <p className="text-xs text-neutral-400">Open Rate</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-green-400">
            {item.responseRate}%
          </p>
          <p className="text-xs text-neutral-400">Response</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-purple-400">
            {item.brandScore}
          </p>
          <p className="text-xs text-neutral-400">Brand Score</p>
        </div>
      </div>

      {/* Compliance Issues */}
      {item.complianceIssues.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 text-orange-400 mb-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">Compliance Issues</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {item.complianceIssues.map((issue, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-orange-500/20 text-orange-300 text-xs rounded-full"
              >
                {issue}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <div className="flex items-center space-x-2">
          <User className="w-3 h-3" />
          <span>{item.requester}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Clock className="w-3 h-3" />
          <span>{item.timeLeft}</span>
        </div>
      </div>

      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

const ReviewPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [sortBy, setSortBy] = useState<'priority' | 'deadline' | 'created'>('priority');
  const [stats, setStats] = useState<ReviewStats>({
    totalItems: 15,
    pendingReview: 1,
    inReview: 1,
    overdue: 0,
    avgQualityScore: 92,
    approvalRate: 87.5,
    avgTimeToReview: 2.3
  });

  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([
    {
      id: 'review-001',
      type: 'email',
      title: 'TechCorp Growth Strategy Outreach',
      content: 'Hi John, I noticed TechCorp is expanding rapidly in the tech space. We\'ve helped similar companies reduce operational costs by 25-35% while scaling efficiently. Given your role as VP of Engineering, I thought you might be interested in a brief conversation about how this applies to TechCorp. Would you be open to a 15-minute call this week? Best regards, Alex',
      status: 'pending',
      priority: 'high',
      openRate: 28.5,
      responseRate: 12.3,
      brandScore: 92,
      requester: 'Sarah Johnson',
      createdAt: '2024-01-24',
      deadline: '2024-01-25',
      timeLeft: '23h left',
      complianceIssues: []
    },
    {
      id: 'review-002',
      type: 'sms',
      title: 'TechCorp Follow-up SMS',
      content: 'Hi John, quick question about TechCorp\'s tech strategy. 5 min call? Reply STOP to opt out',
      status: 'in-review',
      priority: 'medium',
      openRate: 95.0,
      responseRate: 15.2,
      brandScore: 88,
      requester: 'Lisa Wang',
      assignedTo: 'Mike Chen',
      createdAt: '2024-01-24',
      deadline: '2024-01-26',
      timeLeft: '1d left',
      complianceIssues: ['Compliance Issues']
    }
  ]);

  const handleReview = (item: ReviewItem) => {
    console.log('Review item:', item.title);
  };

  const handleApprove = (item: ReviewItem) => {
    console.log('Approve item:', item.title);
  };

  const handleReject = (item: ReviewItem) => {
    console.log('Reject item:', item.title);
  };

  const filteredItems = reviewItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All' || item.status === statusFilter.toLowerCase().replace(' ', '-');
    const matchesType = typeFilter === 'All' || item.type === typeFilter.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const statusCounts = {
    All: reviewItems.length,
    Pending: reviewItems.filter(item => item.status === 'pending').length,
    'In Review': reviewItems.filter(item => item.status === 'in-review').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
            <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              Content Review
            </h1>
            <p className="text-xl text-neutral-400">
                Review and approve AI-generated content before deployment
              </p>
            </div>
            <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-4 text-sm text-neutral-400">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>Pending</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>In Review</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span>Overdue</span>
                </div>
              </div>
            </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <MetricCard
            title="Pending Review"
            value={stats.pendingReview}
            change="New items today"
            icon={Clock}
            gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
            description="Awaiting review"
          />
          <MetricCard
            title="Under Review"
            value={stats.inReview}
            change="Being processed"
            icon={Eye}
            gradient="bg-gradient-to-r from-purple-500 to-pink-500"
            description="Currently reviewing"
          />
          <MetricCard
            title="Overdue"
            value={stats.overdue}
            change="Past deadline"
            icon={AlertTriangle}
            gradient="bg-gradient-to-r from-red-500 to-orange-500"
            description="Needs attention"
          />
          <MetricCard
            title="Avg Quality Score"
            value={`${stats.avgQualityScore}%`}
            change="+5% vs last week"
            icon={Award}
            gradient="bg-gradient-to-r from-green-500 to-emerald-500"
            description="Content quality"
          />
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 mb-8 shadow-xl"
        >
          <div className="flex flex-col space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search content..."
                className="w-full pl-10 pr-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-6">
              {/* Status Filter */}
              <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      statusFilter === status
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {status}
                    {count > 0 && (
                      <span className="ml-2 text-xs bg-neutral-600 text-neutral-300 px-1.5 py-0.5 rounded-full">
                        {count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Type Filter */}
              <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
                {['All', 'Email', 'SMS', 'LinkedIn', 'Voicemail'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setTypeFilter(type)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      typeFilter === type
                        ? 'bg-purple-500 text-white shadow-lg'
                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                    )}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Review Items Grid */}
        {filteredItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-3xl font-semibold text-white mb-4">All caught up!</h3>
            <p className="text-neutral-400 mb-8 max-w-md mx-auto">
              {searchQuery 
                ? 'No items match your search criteria' 
                : 'No items require review at the moment'
              }
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
          >
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <ReviewItemCard
                  item={item}
                  onReview={handleReview}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ReviewPage; 